import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';
import {
  TrendingUp, MapPin, Truck, Zap, Download, RefreshCw,
  Filter, Phone, Mail, Linkedin, Globe, AlertTriangle,
  ChevronDown, ChevronUp, X, CheckCircle, Clock, Star, Sparkles
} from 'lucide-react';

/* ═══════════════════════════════════════════════
   THEME
   ═══════════════════════════════════════════════ */
const FLEET_COLOR  = '#F59E0B';
const FLEET_DARK   = '#D97706';
const HOT_COLOR    = '#EF4444';
const WARM_COLOR   = '#F97316';
const COLD_COLOR   = '#6B7280';

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA',
  'HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
];

const SOURCES = [
  { value: 'fmcsa',        label: 'FMCSA / SAFER System' },
  { value: 'linkedin',     label: 'LinkedIn Sales Navigator' },
  { value: 'indeed',       label: 'Indeed / ZipRecruiter' },
  { value: 'ziprecruiter', label: 'ZipRecruiter' },
  { value: 'google_maps',  label: 'Google Maps' },
];

const INDUSTRIES = [
  'Logistics', 'Trucking', 'Transportation', 'Construction',
  'HVAC', 'Delivery', 'Courier', 'Medical Transport',
  'Utilities', 'Food Distribution', 'Landscaping', 'Waste Management',
];

/* ═══════════════════════════════════════════════
   ANIMATIONS
   ═══════════════════════════════════════════════ */
const fadeIn = keyframes`from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); }`;
const spin = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;
const pulse = keyframes`0%,100% { opacity: 1; } 50% { opacity: 0.5; }`;

/* ═══════════════════════════════════════════════
   STYLED COMPONENTS
   ═══════════════════════════════════════════════ */
const Page = styled.div`
  width: 100%;
  animation: ${fadeIn} 0.25s ease;
`;

const Header = styled.div`
  display: flex; align-items: center; gap: 12px;
  margin-bottom: 22px;
`;

const HeaderTitle = styled.h1`
  font-size: 20px; font-weight: 800; margin: 0;
  color: var(--text-primary);
  display: flex; align-items: center; gap: 10px;
  svg { color: ${FLEET_COLOR}; }
`;

const HeaderSub = styled.p`
  font-size: 12px; color: var(--text-muted); margin: 2px 0 0;
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
  gap: 12px;
  margin-bottom: 22px;
`;

const StatCard = styled.div`
  background: var(--card-bg); border: 1px solid var(--card-border);
  border-radius: 12px; padding: 14px 16px;
  border-top: 3px solid ${p => p.$color || 'var(--primary)'};
`;

const StatVal = styled.div`
  font-size: 24px; font-weight: 800; color: ${p => p.$color || 'var(--text-primary)'};
  line-height: 1;
`;

const StatLabel = styled.div`
  font-size: 11px; color: var(--text-muted); margin-top: 4px; font-weight: 500;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 340px 1fr;
  gap: 18px;
  @media (max-width: 1100px) { grid-template-columns: 1fr; }
`;

const Card = styled.div`
  background: var(--card-bg); border: 1px solid var(--card-border);
  border-radius: 14px; overflow: hidden;
`;

const CardHead = styled.div`
  padding: 14px 18px; border-bottom: 1px solid var(--border);
  display: flex; align-items: center; justify-content: space-between;
  font-weight: 700; font-size: 13px; color: var(--text-primary);
  .left { display: flex; align-items: center; gap: 8px; svg { color: ${FLEET_COLOR}; } }
`;

const CardBody = styled.div`
  padding: 16px;
`;

/* ── Scraping Job Form ── */
const FormGroup = styled.div`
  margin-bottom: 14px;
  label { display: block; font-size: 11px; font-weight: 600; color: var(--text-muted); margin-bottom: 5px; text-transform: uppercase; letter-spacing: 0.5px; }
`;

const Select = styled.select`
  width: 100%; padding: 9px 12px; border-radius: 8px;
  border: 1px solid var(--border); background: var(--input-bg);
  color: var(--text-primary); font-size: 13px;
  &:focus { outline: none; border-color: ${FLEET_COLOR}; }
`;

const Input = styled.input`
  width: 100%; padding: 9px 12px; border-radius: 8px;
  border: 1px solid var(--border); background: var(--input-bg);
  color: var(--text-primary); font-size: 13px; box-sizing: border-box;
  &:focus { outline: none; border-color: ${FLEET_COLOR}; }
`;

const LaunchBtn = styled.button`
  width: 100%; padding: 11px; border-radius: 10px; border: none;
  background: linear-gradient(135deg, ${FLEET_COLOR}, ${FLEET_DARK});
  color: #fff; font-size: 14px; font-weight: 700; cursor: pointer;
  display: flex; align-items: center; justify-content: center; gap: 8px;
  transition: opacity 0.2s, transform 0.15s;
  &:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  ${p => p.$spinning && css`svg { animation: ${spin} 1s linear infinite; }`}
`;

const JobStatus = styled.div`
  margin-top: 12px; padding: 10px 14px; border-radius: 8px;
  background: ${p => p.$success ? 'rgba(16,185,129,0.1)' : p.$error ? 'rgba(239,68,68,0.1)' : 'rgba(245,158,11,0.1)'};
  border: 1px solid ${p => p.$success ? '#10B981' : p.$error ? '#EF4444' : FLEET_COLOR};
  color: ${p => p.$success ? '#10B981' : p.$error ? '#EF4444' : FLEET_COLOR};
  font-size: 12px; font-weight: 500;
  ${p => !p.$success && !p.$error && css`animation: ${pulse} 1.5s ease infinite;`}
`;

/* ── Leads Table ── */
const TableWrap = styled.div`
  overflow-x: auto;
`;

const FilterBar = styled.div`
  display: flex; align-items: center; gap: 10px; padding: 12px 16px;
  border-bottom: 1px solid var(--border); flex-wrap: wrap;
`;

const FilterBtn = styled.button`
  padding: 5px 12px; border-radius: 20px; font-size: 11px; font-weight: 600;
  border: 1px solid ${p => p.$act ? p.$color || FLEET_COLOR : 'var(--border)'};
  background: ${p => p.$act ? (p.$color || FLEET_COLOR) + '22' : 'transparent'};
  color: ${p => p.$act ? p.$color || FLEET_COLOR : 'var(--text-muted)'};
  cursor: pointer; transition: all 0.15s;
  &:hover { border-color: ${p => p.$color || FLEET_COLOR}; }
`;

const Table = styled.table`
  width: 100%; border-collapse: collapse; font-size: 12px;
`;

const Th = styled.th`
  padding: 10px 14px; text-align: left; font-size: 10px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.5px; color: var(--text-muted);
  border-bottom: 1px solid var(--border); white-space: nowrap;
  cursor: ${p => p.$sortable ? 'pointer' : 'default'};
  &:hover { color: ${p => p.$sortable ? FLEET_COLOR : 'var(--text-muted)'}; }
`;

const Tr = styled.tr`
  border-bottom: 1px solid var(--border);
  transition: background 0.15s;
  cursor: pointer;
  &:hover { background: var(--surface-hover); }
`;

const Td = styled.td`
  padding: 10px 14px; color: var(--text-primary); vertical-align: middle;
`;

const PriorityBadge = styled.span`
  padding: 3px 9px; border-radius: 12px; font-size: 10px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.5px;
  background: ${p => p.$p === 'hot' ? HOT_COLOR + '22' : p.$p === 'warm' ? WARM_COLOR + '22' : COLD_COLOR + '22'};
  color: ${p => p.$p === 'hot' ? HOT_COLOR : p.$p === 'warm' ? WARM_COLOR : COLD_COLOR};
  border: 1px solid ${p => p.$p === 'hot' ? HOT_COLOR : p.$p === 'warm' ? WARM_COLOR : COLD_COLOR}44;
`;

const ScoreBadge = styled.span`
  display: inline-flex; align-items: center; justify-content: center;
  width: 34px; height: 34px; border-radius: 50%; font-size: 12px; font-weight: 800;
  background: ${p => p.$s >= 70 ? HOT_COLOR : p.$s >= 40 ? WARM_COLOR : COLD_COLOR}22;
  color: ${p => p.$s >= 70 ? HOT_COLOR : p.$s >= 40 ? WARM_COLOR : COLD_COLOR};
  border: 2px solid ${p => p.$s >= 70 ? HOT_COLOR : p.$s >= 40 ? WARM_COLOR : COLD_COLOR}55;
`;

const IconLink = styled.a`
  color: var(--text-muted); &:hover { color: ${FLEET_COLOR}; } margin-right: 6px;
`;

const SignalDot = styled.span`
  display: inline-block; width: 7px; height: 7px; border-radius: 50%;
  background: ${p => p.$on ? '#10B981' : 'var(--border)'};
  margin-right: 5px;
`;

/* ── Detail Drawer ── */
const Drawer = styled.div`
  position: fixed; right: 0; top: 0; height: 100vh; width: 420px;
  background: var(--surface); border-left: 1px solid var(--border);
  z-index: 200; overflow-y: auto; padding: 20px;
  transform: translateX(${p => p.$open ? '0' : '100%'});
  transition: transform 0.25s ease;
  box-shadow: ${p => p.$open ? '-8px 0 32px rgba(0,0,0,0.12)' : 'none'};
`;

const DrawerHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between; margin-bottom: 18px;
`;

const DrawerTitle = styled.h2`
  font-size: 16px; font-weight: 800; color: var(--text-primary); margin: 0;
`;

const CloseBtn = styled.button`
  background: none; border: none; cursor: pointer; color: var(--text-muted);
  padding: 4px; border-radius: 6px;
  &:hover { background: var(--surface-hover); color: var(--text-primary); }
`;

const DrawerSection = styled.div`
  margin-bottom: 20px;
  h4 { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.6px; color: var(--text-muted); margin: 0 0 10px; }
`;

const DrawerRow = styled.div`
  display: flex; align-items: flex-start; gap: 10px; margin-bottom: 8px;
  font-size: 13px; color: var(--text-primary);
  .lbl { color: var(--text-muted); min-width: 110px; font-size: 12px; }
  .val { flex: 1; word-break: break-all; }
`;

const HookBox = styled.div`
  background: ${FLEET_COLOR}15; border: 1px solid ${FLEET_COLOR}44; border-radius: 10px;
  padding: 12px 14px; font-size: 13px; color: var(--text-primary); font-style: italic;
  line-height: 1.5;
`;

const ScoreBar = styled.div`
  margin-bottom: 10px;
  .row { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; font-size: 12px; }
  .lbl { color: var(--text-muted); width: 140px; flex-shrink: 0; }
  .bar-wrap { flex: 1; height: 6px; background: var(--border); border-radius: 3px; overflow: hidden; }
  .bar { height: 100%; border-radius: 3px; background: ${FLEET_COLOR}; transition: width 0.4s; }
  .pts { color: var(--text-primary); font-weight: 700; width: 40px; text-align: right; }
`;

const ActionBar = styled.div`
  display: flex; gap: 8px; margin-top: 16px; flex-wrap: wrap;
`;

const ActionBtn = styled.button`
  flex: 1; min-width: 120px; padding: 9px 12px; border-radius: 8px;
  border: 1px solid ${p => p.$primary ? FLEET_COLOR : 'var(--border)'};
  background: ${p => p.$primary ? FLEET_COLOR : 'transparent'};
  color: ${p => p.$primary ? '#fff' : 'var(--text-primary)'};
  font-size: 12px; font-weight: 600; cursor: pointer;
  display: flex; align-items: center; justify-content: center; gap: 6px;
  &:hover { opacity: 0.85; }
`;

const EmptyState = styled.div`
  padding: 60px 20px; text-align: center; color: var(--text-muted);
  svg { margin-bottom: 12px; opacity: 0.3; }
  p { font-size: 13px; margin: 0; }
`;

const Pagination = styled.div`
  display: flex; align-items: center; gap: 8px; padding: 12px 16px;
  border-top: 1px solid var(--border); font-size: 12px; color: var(--text-muted);
`;

const PageBtn = styled.button`
  padding: 4px 10px; border-radius: 6px; border: 1px solid var(--border);
  background: ${p => p.$act ? FLEET_COLOR : 'transparent'};
  color: ${p => p.$act ? '#fff' : 'var(--text-muted)'};
  cursor: pointer; font-size: 12px;
  &:disabled { opacity: 0.35; cursor: not-allowed; }
`;

const ExportBtn = styled.button`
  padding: 7px 14px; border-radius: 8px;
  border: 1px solid ${FLEET_COLOR}; background: transparent;
  color: ${FLEET_COLOR}; font-size: 12px; font-weight: 600; cursor: pointer;
  display: flex; align-items: center; gap: 6px;
  &:hover { background: ${FLEET_COLOR}15; }
`;

const RescoringBadge = styled.span`
  font-size: 10px; color: ${FLEET_COLOR}; font-weight: 600;
  ${p => p.$running && css`animation: ${pulse} 1s ease infinite;`}
`;

const Spinner = styled(RefreshCw)`
  ${p => p.$on && css`animation: ${spin} 1s linear infinite;`}
`;

/* ═══════════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════════ */
export default function FleetLeads() {
  // ── Leads list ──
  const [leads, setLeads]         = useState([]);
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(1);
  const [loading, setLoading]     = useState(false);

  // ── Stats ──
  const [stats, setStats]         = useState(null);

  // ── Filters ──
  const [filterPriority, setFilterPriority] = useState('');
  const [filterState, setFilterState]       = useState('');
  const [filterSource, setFilterSource]     = useState('');
  const [filterJobId, setFilterJobId]       = useState('');
  const [sortAsc, setSortAsc]               = useState(false);
  const [enrichingIds, setEnrichingIds]     = useState(new Set());

  // ── Scraping Job ──
  const [jobState, setJobState]             = useState('');
  const [jobSource, setJobSource]           = useState('fmcsa');
  const [jobMinUnits, setJobMinUnits]       = useState('5');
  const [jobIndustry, setJobIndustry]       = useState('');
  const [jobCategory, setJobCategory]       = useState('');
  const [jobSkipExisting, setJobSkipExisting] = useState(false);
  const [jobStatus, setJobStatus]           = useState(null);
  const [jobMsg, setJobMsg]                 = useState('');
  const [jobHistory, setJobHistory]         = useState([]);

  // ── Detail drawer ──
  const [selected, setSelected] = useState(null);

  // ── Rescoring ──
  const [rescoring, setRescoring] = useState(false);

  const LIMIT = 50;

  /* ── Fetch leads ── */
  const fetchLeads = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, limit: LIMIT });
      if (filterPriority) params.append('priority', filterPriority);
      if (filterState)    params.append('state', filterState);
      if (filterSource)   params.append('source', filterSource);
      if (filterJobId)    params.append('job_id', filterJobId);
      const r = await fetch(`/api/fleet-leads?${params}`);
      const d = await r.json();
      if (d.success) {
        const sorted = [...(d.leads || [])];
        if (sortAsc) sorted.reverse();
        setLeads(sorted);
        setTotal(d.total || 0);
        setPage(p);
      }
    } finally {
      setLoading(false);
    }
  }, [filterPriority, filterState, filterSource, filterJobId, sortAsc]);

  /* ── Fetch stats ── */
  const fetchStats = useCallback(async () => {
    try {
      const r = await fetch('/api/fleet-leads/stats');
      const d = await r.json();
      if (d.success) setStats(d);
    } catch (_) {}
  }, []);

  /* ── Fetch job history ── */
  const fetchJobHistory = useCallback(async () => {
    try {
      const r = await fetch('/api/fleet-leads/scrape-jobs');
      const d = await r.json();
      if (d.jobs) setJobHistory(d.jobs);
    } catch (_) {}
  }, []);

  useEffect(() => { fetchLeads(1); }, [fetchLeads]);
  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchJobHistory(); }, [fetchJobHistory]);

  /* ── Launch scraping job ── */
  const launchJob = async () => {
    if (!jobState || !jobSource) {
      setJobStatus('error');
      setJobMsg('Sélectionne un État et une source.');
      return;
    }
    setJobStatus('loading');
    setJobMsg(`Lancement du scraping ${jobSource.toUpperCase()} pour ${jobState}…`);

    try {
      const body = {
        source: jobSource,
        state: jobState,
        min_units: Number(jobMinUnits) || 5,
        industry: jobIndustry || null,
        category: jobCategory || null,
        max: 50,
        enrich: false,
        skip_existing: jobSkipExisting,
      };
      const r = await fetch('/api/fleet-leads/scrape-job', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!d.success) throw new Error(d.error || 'Job rejected');

      setJobStatus('success');
      setJobMsg(`Job ${d.job_id} lancé — ${jobSource.toUpperCase()} · ${jobState}${jobSkipExisting ? ' · nouveaux seulement' : ''}`);

      // Poll job status every 3s
      const pollUrl = d.poll_url;
      const poll = setInterval(async () => {
        try {
          const pr = await fetch(pollUrl);
          const pd = await pr.json();
          if (pd.status === 'completed') {
            clearInterval(poll);
            setJobMsg(`✓ Job terminé (${jobSource.toUpperCase()} · ${jobState})`);
            fetchLeads(1);
            fetchStats();
            fetchJobHistory();
          } else if (pd.status === 'failed') {
            clearInterval(poll);
            setJobStatus('error');
            setJobMsg(`✗ Job échoué (exit ${pd.exit_code}) — ${pd.stderr_tail?.slice(-200) || ''}`);
          }
        } catch (_) {}
      }, 3000);
      setTimeout(() => clearInterval(poll), 5 * 60 * 1000);
    } catch (e) {
      setJobStatus('error');
      setJobMsg(`Erreur : ${e.message}`);
    }
  };

  /* ── Re-score all ── */
  const handleRescore = async () => {
    setRescoring(true);
    try {
      await fetch('/api/fleet-leads/score-all', { method: 'POST' });
      await fetchLeads(page);
      await fetchStats();
    } finally {
      setRescoring(false);
    }
  };

  /* ── Export CSV ── */
  const handleExport = () => {
    const params = new URLSearchParams();
    if (filterPriority) params.append('priority', filterPriority);
    if (filterState)    params.append('state', filterState);
    if (filterSource)   params.append('source', filterSource);
    window.open(`/api/fleet-leads/export/csv?${params}`, '_blank');
  };

  /* ── Update lead status ── */
  const updateStatus = async (id, newStatus) => {
    try {
      await fetch(`/api/fleet-leads/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      setLeads(prev => prev.map(l => l.id === id ? { ...l, status: newStatus } : l));
      if (selected?.id === id) setSelected(prev => ({ ...prev, status: newStatus }));
    } catch (_) {}
  };

  /* ── Enrich one lead via Gemini ── */
  const enrichLead = async (id) => {
    setEnrichingIds(prev => new Set(prev).add(id));
    try {
      const res  = await fetch(`/api/fleet-leads/${id}/enrich`, { method: 'POST' });
      const data = await res.json();
      if (data.success && data.lead) {
        setLeads(prev => prev.map(l => l.id === id ? { ...l, ...data.lead } : l));
        if (selected?.id === id) setSelected(prev => ({ ...prev, ...data.lead }));
      } else {
        alert(`Enrichissement échoué : ${data.error || 'erreur inconnue'}`);
      }
    } catch (e) {
      alert(`Erreur : ${e.message}`);
    } finally {
      setEnrichingIds(prev => { const n = new Set(prev); n.delete(id); return n; });
    }
  };

  /* ── Enrich all currently displayed leads (batch) ── */
  const enrichBatch = async () => {
    const toEnrich = leads.filter(l => l.enrichment_status !== 'enriched').map(l => l.id);
    if (!toEnrich.length) {
      alert('Tous les leads affichés sont déjà enrichis.');
      return;
    }
    if (toEnrich.length > 50) {
      if (!window.confirm(`${toEnrich.length} leads — max 50 par batch. Continuer avec les 50 premiers ?`)) return;
    } else if (!window.confirm(`Enrichir ${toEnrich.length} lead(s) via Gemini ? (peut prendre ~${toEnrich.length * 5}s)`)) {
      return;
    }
    const batch = toEnrich.slice(0, 50);
    setEnrichingIds(prev => { const n = new Set(prev); batch.forEach(id => n.add(id)); return n; });
    try {
      const res = await fetch('/api/fleet-leads/enrich-batch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: batch }),
      });
      const data = await res.json();
      if (data.success) {
        alert(`Batch terminé : ${data.enriched} enrichis, ${data.failed} échecs`);
        await fetchLeads(page);
      } else {
        alert(`Erreur batch : ${data.error || 'inconnue'}`);
      }
    } catch (e) {
      alert(`Erreur : ${e.message}`);
    } finally {
      setEnrichingIds(new Set());
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  return (
    <Page>
      {/* ── Header ── */}
      <Header>
        <div>
          <HeaderTitle>
            <TrendingUp size={20} />
            Lead Scraper US Fleet
          </HeaderTitle>
          <HeaderSub>Pipeline B2B · Verizon Connect Telematics · {total} leads total</HeaderSub>
        </div>
      </Header>

      {/* ── Stats row ── */}
      <StatsRow>
        <StatCard $color={FLEET_COLOR}>
          <StatVal $color={FLEET_COLOR}>{stats?.totals?.total ?? '—'}</StatVal>
          <StatLabel>Total leads</StatLabel>
        </StatCard>
        <StatCard $color={HOT_COLOR}>
          <StatVal $color={HOT_COLOR}>{stats?.totals?.hot ?? '—'}</StatVal>
          <StatLabel>🔴 Hot (≥70)</StatLabel>
        </StatCard>
        <StatCard $color={WARM_COLOR}>
          <StatVal $color={WARM_COLOR}>{stats?.totals?.warm ?? '—'}</StatVal>
          <StatLabel>🟠 Warm (≥40)</StatLabel>
        </StatCard>
        <StatCard $color={COLD_COLOR}>
          <StatVal $color={COLD_COLOR}>{stats?.totals?.cold ?? '—'}</StatVal>
          <StatLabel>🔵 Cold</StatLabel>
        </StatCard>
        <StatCard $color="#10B981">
          <StatVal $color="#10B981">{stats?.totals?.with_hiring_signal ?? '—'}</StatVal>
          <StatLabel>Hiring signals</StatLabel>
        </StatCard>
        <StatCard $color="#6366F1">
          <StatVal $color="#6366F1">{stats?.totals?.with_mobile ?? '—'}</StatVal>
          <StatLabel>Mobile direct</StatLabel>
        </StatCard>
        <StatCard $color="#8B5CF6">
          <StatVal $color="#8B5CF6">{stats?.totals?.avg_score ?? '—'}</StatVal>
          <StatLabel>Score moyen</StatLabel>
        </StatCard>
      </StatsRow>

      {/* ── Main Grid ── */}
      <Grid>
        {/* ── Left: Scraping Job Panel ── */}
        <Card>
          <CardHead>
            <div className="left"><Zap size={15} /> Nouveau Scraping Job</div>
          </CardHead>
          <CardBody>
            <FormGroup>
              <label>État US *</label>
              <Select value={jobState} onChange={e => setJobState(e.target.value)}>
                <option value="">— Sélectionner —</option>
                {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </Select>
            </FormGroup>
            <FormGroup>
              <label>Source *</label>
              <Select value={jobSource} onChange={e => setJobSource(e.target.value)}>
                {SOURCES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </Select>
            </FormGroup>
            <FormGroup>
              <label>Min. Power Units (FMCSA)</label>
              <Input
                type="number" min="1" value={jobMinUnits}
                onChange={e => setJobMinUnits(e.target.value)}
                placeholder="ex: 5"
              />
            </FormGroup>
            <FormGroup>
              <label>Industrie cible (optionnel)</label>
              <Select value={jobIndustry} onChange={e => setJobIndustry(e.target.value)}>
                <option value="">— Toutes —</option>
                {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
              </Select>
            </FormGroup>
            {jobSource === 'google_maps' && (
              <FormGroup>
                <label>Catégorie Google Maps</label>
                <Select value={jobCategory} onChange={e => setJobCategory(e.target.value)}>
                  <option value="">trucking (défaut)</option>
                  {['trucking','truck_repair','moving','contractor','hvac','courier','waste','landscaping'].map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </Select>
              </FormGroup>
            )}
            <FormGroup style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 4 }}>
              <input
                type="checkbox"
                id="skipExistingChk"
                checked={jobSkipExisting}
                onChange={e => setJobSkipExisting(e.target.checked)}
                style={{ accentColor: FLEET_COLOR, width: 14, height: 14, cursor: 'pointer' }}
              />
              <label htmlFor="skipExistingChk" style={{ fontSize: 12, color: 'var(--text-secondary)', cursor: 'pointer', fontWeight: 400, margin: 0 }}>
                Nouveaux leads seulement <span style={{ color: 'var(--text-muted)' }}>(ignore les DOTs déjà en base)</span>
              </label>
            </FormGroup>
            <LaunchBtn
              onClick={launchJob}
              disabled={jobStatus === 'loading'}
              $spinning={jobStatus === 'loading'}
            >
              <Zap size={15} />
              {jobStatus === 'loading' ? 'Lancement…' : 'Lancer le Scraping'}
            </LaunchBtn>
            {jobStatus && (
              <JobStatus $success={jobStatus === 'success'} $error={jobStatus === 'error'}>
                {jobStatus === 'success' && <CheckCircle size={13} style={{ marginRight: 6 }} />}
                {jobStatus === 'error' && <AlertTriangle size={13} style={{ marginRight: 6 }} />}
                {jobStatus === 'loading' && <Clock size={13} style={{ marginRight: 6 }} />}
                {jobMsg}
              </JobStatus>
            )}

            {/* ── Session History ── */}
            {jobHistory.length > 0 && (
              <div style={{ marginTop: 18 }}>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-muted)', marginBottom: 6 }}>Sessions récentes</div>
                {jobHistory.slice(0, 6).map(j => (
                  <div
                    key={j.id}
                    onClick={() => { setFilterJobId(j.id === filterJobId ? '' : j.id); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4,
                      fontSize: 11, padding: '5px 8px', borderRadius: 6, cursor: 'pointer',
                      background: j.id === filterJobId ? `${FLEET_COLOR}22` : 'var(--bg-tertiary)',
                      border: `1px solid ${j.id === filterJobId ? FLEET_COLOR : 'transparent'}`,
                      transition: 'all 0.15s',
                    }}
                    title={j.id}
                  >
                    <span style={{ fontWeight: 700, textTransform: 'uppercase', color: FLEET_COLOR, width: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {j.source}·{j.state}
                    </span>
                    <span style={{
                      fontSize: 10, padding: '1px 5px', borderRadius: 3, fontWeight: 700,
                      background: j.status === 'completed' ? '#10b98133' : j.status === 'failed' ? '#ef444433' : '#f59e0b33',
                      color: j.status === 'completed' ? '#10b981' : j.status === 'failed' ? '#ef4444' : '#f59e0b',
                    }}>{j.status}</span>
                    <span style={{ color: 'var(--text-muted)', marginLeft: 'auto', fontSize: 10 }}>
                      {new Date(j.started_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* ── Top States ── */}
            {stats?.byState?.length > 0 && (
              <div style={{ marginTop: 20 }}>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-muted)', marginBottom: 8 }}>Top États</div>
                {stats.byState.slice(0, 8).map(s => (
                  <div key={s.state} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5, fontSize: 12 }}>
                    <MapPin size={11} color={FLEET_COLOR} />
                    <span style={{ fontWeight: 700, width: 28, color: 'var(--text-primary)' }}>{s.state}</span>
                    <div style={{ flex: 1, height: 4, background: 'var(--border)', borderRadius: 2, overflow: 'hidden' }}>
                      <div style={{ width: `${Math.min(100, (s.count / (stats.byState[0]?.count || 1)) * 100)}%`, height: '100%', background: FLEET_COLOR, borderRadius: 2 }} />
                    </div>
                    <span style={{ color: 'var(--text-muted)', width: 28, textAlign: 'right' }}>{s.count}</span>
                  </div>
                ))}
              </div>
            )}
          </CardBody>
        </Card>

        {/* ── Right: Hot Leads Table ── */}
        <Card>
          <CardHead>
            <div className="left"><Star size={15} /> Hot Leads <span style={{ fontWeight: 400, fontSize: 11, color: 'var(--text-muted)', marginLeft: 4 }}>triés par score</span></div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <RescoringBadge $running={rescoring}>{rescoring ? 'Rescoring…' : ''}</RescoringBadge>
              <ExportBtn onClick={handleExport} title="Export CSV Verizon Script Ready">
                <Download size={13} /> Export CSV
              </ExportBtn>
              <ActionBtn
                onClick={enrichBatch}
                disabled={enrichingIds.size > 0}
                style={{ minWidth: 'auto', padding: '7px 10px', background: 'linear-gradient(135deg, #8B5CF6, #6366F1)', color: '#fff', borderColor: 'transparent' }}
                title="Enrichir les leads affichés via Gemini + website scrape"
              >
                <Sparkles size={13} /> {enrichingIds.size > 0 ? `${enrichingIds.size}…` : 'IA Batch'}
              </ActionBtn>
              <ActionBtn onClick={handleRescore} disabled={rescoring} style={{ minWidth: 'auto', padding: '7px 10px' }} title="Recalculer les scores">
                <Spinner size={13} $on={rescoring} />
              </ActionBtn>
            </div>
          </CardHead>

          {/* ── Filter bar ── */}
          <FilterBar>
            <Filter size={13} color="var(--text-muted)" />
            {['', 'hot', 'warm', 'cold'].map(p => (
              <FilterBtn key={p || 'all'} $act={filterPriority === p} $color={p === 'hot' ? HOT_COLOR : p === 'warm' ? WARM_COLOR : p === 'cold' ? COLD_COLOR : FLEET_COLOR}
                onClick={() => { setFilterPriority(p); setPage(1); }}>
                {p ? p.toUpperCase() : 'TOUS'}
              </FilterBtn>
            ))}
            <div style={{ width: 1, height: 16, background: 'var(--border)' }} />
            <Select value={filterState} onChange={e => { setFilterState(e.target.value); setPage(1); }} style={{ width: 80, padding: '4px 8px', fontSize: 11 }}>
              <option value="">🌎 State</option>
              {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
            </Select>
            <Select value={filterSource} onChange={e => { setFilterSource(e.target.value); setPage(1); }} style={{ width: 140, padding: '4px 8px', fontSize: 11 }}>
              <option value="">📡 Source</option>
              {SOURCES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </Select>
            {filterJobId && (
              <FilterBtn $act $color={FLEET_COLOR} style={{ fontSize: 10 }} onClick={() => { setFilterJobId(''); setPage(1); }}>
                🎯 Session active <X size={10} />
              </FilterBtn>
            )}
            {(filterPriority || filterState || filterSource || filterJobId) && (
              <FilterBtn onClick={() => { setFilterPriority(''); setFilterState(''); setFilterSource(''); setFilterJobId(''); setPage(1); }}>
                <X size={11} /> Reset
              </FilterBtn>
            )}
          </FilterBar>

          <TableWrap>
            {loading ? (
              <EmptyState><Spinner size={28} $on /><p>Chargement…</p></EmptyState>
            ) : leads.length === 0 ? (
              <EmptyState>
                <TrendingUp size={32} />
                <p>Aucun lead trouvé.<br />Lance un Scraping Job pour commencer.</p>
              </EmptyState>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th $sortable onClick={() => { setSortAsc(!sortAsc); }}>
                      Score {sortAsc ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
                    </Th>
                    <Th>Priorité</Th>
                    <Th>Entreprise</Th>
                    <Th>État</Th>
                    <Th>Power Units</Th>
                    <Th>Safety Rating</Th>
                    <Th>Hiring</Th>
                    <Th>Contact</Th>
                    <Th>Source</Th>
                    <Th>Statut</Th>
                    <Th>IA</Th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map(lead => (
                    <Tr key={lead.id} onClick={() => setSelected(lead)}>
                      <Td><ScoreBadge $s={lead.lead_score}>{lead.lead_score}</ScoreBadge></Td>
                      <Td><PriorityBadge $p={lead.priority}>{lead.priority}</PriorityBadge></Td>
                      <Td>
                        <div style={{ fontWeight: 700, fontSize: 12, color: 'var(--text-primary)' }}>{lead.company_name}</div>
                        {lead.decision_maker_name && <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{lead.decision_maker_name}{lead.decision_maker_title ? ` · ${lead.decision_maker_title}` : ''}</div>}
                      </Td>
                      <Td style={{ fontWeight: 600 }}>{lead.state || '—'}</Td>
                      <Td>
                        <div style={{ fontWeight: 700 }}>{lead.power_units ?? '—'}</div>
                        {lead.fleet_size_category && <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{lead.fleet_size_category}</div>}
                      </Td>
                      <Td>
                        {lead.safety_rating ? (
                          <span style={{ color: ['conditional','unsatisfactory'].includes((lead.safety_rating||'').toLowerCase()) ? HOT_COLOR : '#10B981', fontWeight: 600, fontSize: 11 }}>
                            {lead.safety_rating}
                          </span>
                        ) : '—'}
                      </Td>
                      <Td>
                        <SignalDot $on={lead.hiring_signal} />
                        {lead.hiring_signal ? <span style={{ color: '#10B981', fontWeight: 600, fontSize: 10 }}>OUI</span> : <span style={{ color: COLD_COLOR, fontSize: 10 }}>—</span>}
                      </Td>
                      <Td>
                        {lead.verified_mobile && <IconLink href={`tel:${lead.verified_mobile}`} onClick={e => e.stopPropagation()} title={lead.verified_mobile}><Phone size={13} /></IconLink>}
                        {lead.professional_email && <IconLink href={`mailto:${lead.professional_email}`} onClick={e => e.stopPropagation()} title={lead.professional_email}><Mail size={13} /></IconLink>}
                        {lead.linkedin_profile && <IconLink href={lead.linkedin_profile} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}><Linkedin size={13} /></IconLink>}
                        {lead.website && <IconLink href={lead.website} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}><Globe size={13} /></IconLink>}
                        {!lead.verified_mobile && !lead.professional_email && !lead.linkedin_profile && '—'}
                      </Td>
                      <Td style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase' }}>{lead.source || '—'}</Td>
                      <Td>
                        <select
                          value={lead.status || 'new'}
                          onClick={e => e.stopPropagation()}
                          onChange={e => { e.stopPropagation(); updateStatus(lead.id, e.target.value); }}
                          style={{ fontSize: 11, padding: '3px 6px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--input-bg)', color: 'var(--text-primary)' }}
                        >
                          {['new','contacted','interested','demo','won','lost'].map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </Td>
                      <Td>
                        <button
                          onClick={e => { e.stopPropagation(); enrichLead(lead.id); }}
                          disabled={enrichingIds.has(lead.id) || lead.enrichment_status === 'enriched'}
                          title={lead.enrichment_status === 'enriched' ? 'Déjà enrichi par IA' : 'Enrichir via Gemini'}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            padding: '4px 8px', fontSize: 10, fontWeight: 600,
                            borderRadius: 6, border: 'none', cursor: enrichingIds.has(lead.id) ? 'wait' : 'pointer',
                            background: lead.enrichment_status === 'enriched' ? '#10B981' : 'linear-gradient(135deg, #8B5CF6, #6366F1)',
                            color: '#fff', opacity: enrichingIds.has(lead.id) ? 0.6 : 1,
                          }}
                        >
                          <Sparkles size={11} />
                          {enrichingIds.has(lead.id) ? '...' : (lead.enrichment_status === 'enriched' ? 'OK' : 'IA')}
                        </button>
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            )}
          </TableWrap>

          {/* ── Pagination ── */}
          {totalPages > 1 && (
            <Pagination>
              <PageBtn disabled={page <= 1} onClick={() => fetchLeads(page - 1)}>← Préc</PageBtn>
              <span>{page} / {totalPages} ({total} leads)</span>
              <PageBtn disabled={page >= totalPages} onClick={() => fetchLeads(page + 1)}>Suiv →</PageBtn>
            </Pagination>
          )}
        </Card>
      </Grid>

      {/* ── Detail Drawer ── */}
      <Drawer $open={!!selected}>
        {selected && (
          <>
            <DrawerHeader>
              <DrawerTitle>Fiche Lead</DrawerTitle>
              <CloseBtn onClick={() => setSelected(null)}><X size={18} /></CloseBtn>
            </DrawerHeader>

            {/* Score breakdown */}
            <DrawerSection>
              <h4>Score · {selected.lead_score}/100 — <PriorityBadge $p={selected.priority}>{selected.priority}</PriorityBadge></h4>
              <ScoreBar>
                {[
                  { lbl: 'Fleet Size (40%)', val: selected.score_fleet_size, max: 40 },
                  { lbl: 'Growth Signal (30%)', val: selected.score_growth_signal, max: 30 },
                  { lbl: 'Data Completeness (20%)', val: selected.score_data_completeness, max: 20 },
                  { lbl: 'Industry Match (10%)', val: selected.score_industry_match, max: 10 },
                ].map(r => (
                  <div className="row" key={r.lbl}>
                    <span className="lbl">{r.lbl}</span>
                    <div className="bar-wrap"><div className="bar" style={{ width: `${(r.val / r.max) * 100}%` }} /></div>
                    <span className="pts">{r.val}/{r.max}</span>
                  </div>
                ))}
              </ScoreBar>
            </DrawerSection>

            {/* Hook */}
            {selected.hook_phrase && (
              <DrawerSection>
                <h4>Opening Line (Hook)</h4>
                <HookBox>"{selected.hook_phrase}"</HookBox>
              </DrawerSection>
            )}

            {/* Identity */}
            <DrawerSection>
              <h4>Identité & Contact</h4>
              {[
                { lbl: 'Entreprise', val: selected.company_name },
                { lbl: 'DOT Number', val: selected.dot_number },
                { lbl: 'Decision Maker', val: selected.decision_maker_name },
                { lbl: 'Titre', val: selected.decision_maker_title },
                { lbl: 'Mobile Direct', val: selected.verified_mobile },
                { lbl: 'Email Pro', val: selected.professional_email },
                { lbl: 'LinkedIn', val: selected.linkedin_profile },
                { lbl: 'Website', val: selected.website },
              ].filter(r => r.val).map(r => (
                <DrawerRow key={r.lbl}>
                  <span className="lbl">{r.lbl}</span>
                  <span className="val">{r.val}</span>
                </DrawerRow>
              ))}
            </DrawerSection>

            {/* Fleet */}
            <DrawerSection>
              <h4>Fleet Intelligence</h4>
              {[
                { lbl: 'Power Units', val: selected.power_units },
                { lbl: 'Catégorie', val: selected.fleet_size_category },
                { lbl: 'Vehicle Type', val: selected.vehicle_type },
                { lbl: 'Safety Rating', val: selected.safety_rating },
                { lbl: 'OOS Rate', val: selected.out_of_service_rate ? `${selected.out_of_service_rate}%` : null },
                { lbl: 'Crashes (24m)', val: selected.crashes_last_24_months },
                { lbl: 'DOT Status', val: selected.dot_operating_status },
                { lbl: 'Industrie', val: selected.industry },
                { lbl: 'Lieu', val: [selected.city, selected.state, selected.zip_code].filter(Boolean).join(', ') },
              ].filter(r => r.val != null && r.val !== '').map(r => (
                <DrawerRow key={r.lbl}>
                  <span className="lbl">{r.lbl}</span>
                  <span className="val">{r.val}</span>
                </DrawerRow>
              ))}
              {selected.hiring_signal && (
                <DrawerRow>
                  <span className="lbl">Hiring Signal</span>
                  <span className="val" style={{ color: '#10B981', fontWeight: 700 }}>✓ {selected.hiring_job_title || 'CDL Driver'}</span>
                </DrawerRow>
              )}
            </DrawerSection>

            {/* CRM Actions */}
            <DrawerSection>
              <h4>CRM Pipeline</h4>
              <DrawerRow>
                <span className="lbl">Statut</span>
                <select
                  value={selected.status || 'new'}
                  onChange={e => updateStatus(selected.id, e.target.value)}
                  style={{ fontSize: 12, padding: '4px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--input-bg)', color: 'var(--text-primary)' }}
                >
                  {['new','contacted','interested','demo','won','lost'].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </DrawerRow>
              <DrawerRow><span className="lbl">Source</span><span className="val" style={{ textTransform: 'uppercase', fontSize: 11 }}>{selected.source}</span></DrawerRow>
              <DrawerRow><span className="lbl">Scraping</span><span className="val" style={{ fontSize: 11 }}>{selected.scraped_at ? new Date(selected.scraped_at).toLocaleDateString('fr-FR') : '—'}</span></DrawerRow>
            </DrawerSection>

            <ActionBar>
              {selected.verified_mobile && (
                <ActionBtn as="a" href={`tel:${selected.verified_mobile}`} $primary>
                  <Phone size={13} /> {selected.verified_mobile}
                </ActionBtn>
              )}
              {selected.professional_email && (
                <ActionBtn as="a" href={`mailto:${selected.professional_email}`}>
                  <Mail size={13} /> Email
                </ActionBtn>
              )}
              {selected.linkedin_profile && (
                <ActionBtn as="a" href={selected.linkedin_profile} target="_blank" rel="noopener noreferrer">
                  <Linkedin size={13} /> LinkedIn
                </ActionBtn>
              )}
            </ActionBar>
          </>
        )}
      </Drawer>
    </Page>
  );
}
